import { Box, Grid, Stack, Tab, Tabs } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader/PageHeader";
import { MenuRender } from "../../components/RestaurantMenuPage/MenuRender/MenuRender";
import AppNavbar from "../../components/shared/AppNavbar/AppNavbar";
import AddressSelectionModal from "../../components/shared/Modals/AddressSelectionModal/AddressSelectionModal";
import RestaurantErrorModal from "../../components/shared/Modals/RestaurantErrorModal/RestaurantErrorModal";
import ShiftInfo from "../../components/shared/ShiftInfo/ShiftInfo";
import {
  MODE_DELIVERY,
  MODE_ONSITE,
  MODE_TAKEAWAY,
  typeOrderTabs,
} from "../../constants/constants";
import { useConfig } from "../../context/ConfigContext";
import withNavigateHook from "../../hooks/withNavigateHook";
import { languageData } from "../../i18n";
import {
  Category,
  Restaurant,
  RestaurantCart,
} from "../../interfaces/Catalogue";
import { shopApi } from "../../services/apiGate";
import { catalogueApi } from "../../services/catalogueApi";
import { deliveryApi } from "../../services/deliveryApi";
import { loyalityApi } from "../../services/loyalityApi";
import {
  addShopToFavorite,
  categoryShopsChangeShopMode,
  clearStoreItem,
  logout,
} from "../../store/actions/actions";
import { getCategoriesRequest } from "../../store/categories/categoriesActions";
import { setDeliveryAddress } from "../../store/delivery/deliveryActions";
import { filterCategoriesByTypeOrder } from "../../utils/menuUtils";
import { navigateBasedOnRestaurantCount } from "../../utils/navigationUtils";
import { findCurrentShift } from "../../utils/shopSubtittleFormat";
import "./RestaurantMenuPage.scss";

const RestaurantMenuPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { restaurantId } = useParams();

  const { config } = useConfig();
  const location = useLocation();
  
  // Redux state using useSelector
  const userInfo = useSelector((state: any) => state.auth.userInfo);
  const authLocation = useSelector((state: any) => state.auth.location);
  const menuMode = useSelector((state: any) => state.menu.mode);
  const savedCategories = useSelector((state: any) => state.cart.savedCategories);
  
  const restaurantCart: RestaurantCart = savedCategories[Number(restaurantId)] || {};
  const { mode, selectedCategory } = location.state || {};

  const { establishementCount: restaurantCount = 0, isGlobalWebsite = false } =
    config || {};

  const { categories } = useSelector((state: any) => state.categories);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [typeOrder, setTypeOrder] = useState(mode || MODE_TAKEAWAY);
  const [loadingState, setLoadingState] = useState<any>(false);
  const [currentShifts, setCurrentShifts] = useState<serviceMenuModels.Shift>();
  const [RestorentFilter, setRestorentFilter] = useState("");
  const [restaurant, setRestaurant] = useState<Restaurant>();
  const [establishementToken, setEstablishementToken] = useState(
    localStorage.getItem("establishementToken")
  );
  const filteredCategories = filterCategoriesByTypeOrder(categories, typeOrder);
  const [userLoyalityPoints, setUserLoyalityPoints] = useState(0);
  const hasInitialScrolled = useRef(false);

  // Enhanced scrolling refs and state
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isScrollingProgrammatically = useRef(false);
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const tabsContainerRef = useRef<HTMLElement | null>(null);

  // Action dispatchers
  const clearItemStore = (item: string) => dispatch(clearStoreItem(item));

  // Enhanced scroll detection with improved algorithm
  const detectActiveSection = useCallback(() => {
    if (isScrollingProgrammatically.current) return;

    const scrollPosition = window.scrollY;
    const viewportHeight = window.innerHeight;
    const headerOffset = 200; // Adjust based on your sticky header height

    let activeIndex = 0;
    let maxVisibility = 0;

    sectionRefs.current.forEach((sectionElement, index) => {
      if (!sectionElement) return;

      const rect = sectionElement.getBoundingClientRect();
      const sectionTop = rect.top + scrollPosition;
      const sectionBottom = sectionTop + rect.height;
      
      // Calculate how much of the section is visible
      const visibleTop = Math.max(sectionTop, scrollPosition + headerOffset);
      const visibleBottom = Math.min(sectionBottom, scrollPosition + viewportHeight);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const visibilityRatio = visibleHeight / rect.height;

      // Prefer sections that are more visible, with bias towards sections near the top
      const topBias = rect.top <= headerOffset ? 1.2 : 1;
      const adjustedVisibility = visibilityRatio * topBias;

      if (adjustedVisibility > maxVisibility && visibilityRatio > 0.1) {
        maxVisibility = adjustedVisibility;
        activeIndex = index;
      }
    });

    if (activeIndex !== activeTabIndex) {
      setActiveTabIndex(activeIndex);
      scrollTabIntoView(activeIndex);
    }
  }, [activeTabIndex]);

  // Throttled scroll handler for better performance
  const handleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    scrollTimeoutRef.current = setTimeout(() => {
      detectActiveSection();
    }, 10); // Small delay for performance
  }, [detectActiveSection]);

  // Scroll active tab into view in the tabs container
  const scrollTabIntoView = useCallback((index: number) => {
    if (tabsContainerRef.current) {
      const tabElement = tabsContainerRef.current.querySelector(
        `[data-tab-index="${index}"]`
      ) as HTMLElement;
      
      if (tabElement) {
        const container = tabsContainerRef.current.querySelector('.MuiTabs-scroller');
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const tabRect = tabElement.getBoundingClientRect();
          
          if (tabRect.left < containerRect.left || tabRect.right > containerRect.right) {
            tabElement.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center'
            });
          }
        }
      }
    }
  }, []);

  // Enhanced scroll to section with smooth scrolling
  const scrollToSection = useCallback((index: number) => {
    const sectionElement = sectionRefs.current[index];
    if (!sectionElement) return;

    isScrollingProgrammatically.current = true;
    setActiveTabIndex(index);

    const headerOffset = 180; // Adjust based on your sticky elements
    const targetPosition = sectionElement.offsetTop - headerOffset;

    window.scrollTo({
      top: Math.max(0, targetPosition),
      behavior: 'smooth'
    });

    // Re-enable automatic scroll detection after smooth scroll completes
    setTimeout(() => {
      isScrollingProgrammatically.current = false;
    }, 1000);
  }, []);

  // Add this useEffect for clean initialization only
useEffect(() => {
  // Only run once during initialization
  if (
    !hasInitialScrolled.current && 
    selectedCategory && 
    filteredCategories.length > 0 && 
    restaurant && 
    !loadingState &&
    sectionRefs.current.length > 0
  ) {
    const categoryIndex = filteredCategories.findIndex(
      (category: Category) => 
        category.id === selectedCategory.id || 
        category.designation === selectedCategory.designation ||
        category.designation.toLowerCase() === selectedCategory.designation?.toLowerCase()
    );
    
    if (categoryIndex !== -1) {
      // Mark as scrolled to prevent future auto-scrolls
      hasInitialScrolled.current = true;
      
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        // Temporarily disable scroll detection during initial scroll
        isScrollingProgrammatically.current = true;
        setActiveTabIndex(categoryIndex);
        
        const sectionElement = sectionRefs.current[categoryIndex];
        if (sectionElement) {
          const headerOffset = 180;
          const targetPosition = sectionElement.offsetTop - headerOffset;
          
          window.scrollTo({
            top: Math.max(0, targetPosition),
            behavior: 'smooth'
          });
          
          // Re-enable scroll detection after initial scroll
          setTimeout(() => {
            isScrollingProgrammatically.current = false;
          }, 1000);
        }
      }, 400);
    }
  }
}, [filteredCategories, selectedCategory, restaurant, loadingState]);

// Reset the flag when component unmounts or restaurant changes
useEffect(() => {
  hasInitialScrolled.current = false;
}, [restaurantId]);

  // Initialize section refs when categories change
  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, filteredCategories.length);
  }, [filteredCategories.length]);


  // Set up scroll listener
  useEffect(() => {
    const handleScrollEvent = () => handleScroll();
    
    window.addEventListener('scroll', handleScrollEvent, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScrollEvent);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const fetchRestaurant = async () => {
      setLoadingState(true);
      try {
        const res = await shopApi.getResturantById({
          id: Number(restaurantId),
          diningMode: typeOrder,
          adresse: {
            latitude: authLocation?.lat,
            longitude: authLocation?.lng,
          },
        });
        const azureUrl = res?.data?.restaurant?.azureUrl;

        const tokenRes = await catalogueApi.getEstablishementToken(azureUrl);
        const newToken = tokenRes.data.data.result;

        localStorage.setItem("establishementToken", newToken);
        setEstablishementToken(newToken);
        setRestaurant(res?.data?.restaurant);
        setCurrentShifts(findCurrentShift(res?.data?.restaurant));
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoadingState(false);
      }
    };

    if (restaurantId) {
      fetchRestaurant();
    }
  }, [restaurantId, menuMode, authLocation]);

  useEffect(() => {
    if (restaurant?.azureUrl && establishementToken) {
      dispatch(getCategoriesRequest(restaurant?.azureUrl || ""));
    }
  }, [restaurant, establishementToken]);

  useEffect(() => {
    const fetchClientData = async () => {
      if (!restaurant) return;

      try {
        const res = await loyalityApi.getClientBySecretCode(
          userInfo?.qrCodeValue,
          restaurant.azureUrl
        );

        setUserLoyalityPoints(res?.data?.loyalityPoints);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (userInfo?.qrCodeValue) {
      fetchClientData();
    }
  }, [restaurant]);

  const filteredTabs = typeOrderTabs?.filter((tab) => {
    switch (tab.type) {
      case MODE_TAKEAWAY:
        return restaurant?.aEmporte || false;
      case MODE_ONSITE:
        return restaurant?.surPlace || false;
      case MODE_DELIVERY:
        return restaurant?.livraison || false;
      default:
        return false;
    }
  });

  const [showModal, setShowModal] = useState(
    mode == MODE_DELIVERY && restaurantCart.mode != MODE_DELIVERY
  );
  const [showRestaurantError, setShowRestaurantError] = useState(false);

  const handleShowErrorModal = () => {
    setShowRestaurantError(true);
  };

  const handleCloseErrorModal = () => {
    setShowRestaurantError(false);
  };

  const handlePressbtnModal = () => {
    setShowRestaurantError(false);
    setShowModal(true);
  };

  const checkRestaurantDelivery = async (
    address: google.maps.places.PlaceResult | undefined
  ) => {
    deliveryApi
      .checkRestaurantDelivery(String(restaurantId), address)
      .then((res) => {
        setTypeOrder(MODE_DELIVERY);
        console.log("Confirmed Address: ", address);
        dispatch(setDeliveryAddress(address));
        setShowModal(false);
        setShowRestaurantError(false);
      })
      .catch((error) => {
        console.log("error", error);
        handleShowErrorModal();
        setShowRestaurantError(true);
        setShowModal(false);
      });
  };

  return (
    <div className="restaurant-details">
      {/* Establishment Navbar */}
      <AppNavbar
        setRestorentFilter={setRestorentFilter}
        RestorentFilter={RestorentFilter}
      />

      {/* Establishment Grid */}
      <Grid className="restaurant-grid">
        <Grid className="listing-menus">
          {/* Establishment Header*/}
          <PageHeader
            title={restaurant?.nom}
            subtitle={
              loadingState ? (
                "Loading..."
              ) : (
                <>
                  <ShiftInfo currentShifts={currentShifts} />
                  {restaurant?.distance}Km • {restaurant?.tempsPrep} min{" "}
                  {restaurant?.specialites ? "•" + restaurant?.specialites : null}
                </>
              )
            }
            backAction={() => {
              clearItemStore("menu");
              navigateBasedOnRestaurantCount(
                restaurantCount,
                isGlobalWebsite,
                navigate
              );
            }}
          />

          {/*  Categories Tabs*/}
          <Box
            ref={tabsContainerRef}
            sx={{
              backgroundColor: "white",
              position: "sticky",
              zIndex: 2,
              top: "125px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            <Tabs
              className="tabContainer row MuiTabs-scrollableX tabContainer-menu-items"
              value={activeTabIndex}
              variant="scrollable"
              allowScrollButtonsMobile
              selectionFollowsFocus
              scrollButtons="auto"
              sx={{
                "& .MuiTabs-scrollButtons": {
                  width: "12px",
                },
                "& .MuiTabs-indicator": {
                  transition: "all 0.3s ease",
                },
              }}
            >
              {filteredCategories?.map((category: any, index: number) => {
                return (
                  <Tab
                    className={`${index === activeTabIndex ? "current-active-tab" : ""}`}
                    key={index}
                    value={index}
                    data-tab-index={index}
                    onClick={() => scrollToSection(index)}
                    label={category.designation.toLowerCase()}
                    sx={{
                      transition: "all 0.2s ease",
                      minWidth: "auto",
                      "&.Mui-selected": {
                        fontWeight: 600,
                      },
                    }}
                  />
                );
              })}
            </Tabs>
          </Box>

          {/* Type Order Tabs */}
          <section className="filter-cat-by-place tabs-menu-section">
            {filteredTabs.length > 0 && (
              <div className="wrapper-tab">
                {filteredTabs.map((typeOrderTab, index) => (
                  <p
                    key={index}
                    className={
                      typeOrder == typeOrderTab.type ? "active-tab" : ""
                    }
                    onClick={() => {
                      if (typeOrderTab.type == MODE_DELIVERY) {
                        setShowModal(true);
                      } else {
                        setTypeOrder(typeOrderTab.type);
                      }
                    }}
                  >
                    {typeOrderTab.label}
                  </p>
                ))}
              </div>
            )}
          </section>

          {/* categories Grid */}
          <Stack
            className="menuGrid"
            spacing={4}
            direction="column"
            sx={{ marginTop: "2rem" }}
          >
            {filteredCategories?.map((category: Category, i: number) => {
              return (
                <Stack
                  className="menuSection full-width menuSectionContainer"
                  key={i}
                  ref={(el) => (sectionRefs.current[i] = el)}
                  id={`menuSection${i}`}
                  spacing={2}
                  sx={{
                    scrollMarginTop: "200px", // Better scroll positioning
                  }}
                >
                  <div className="name-cat">
                    <p>{category.designation.toLowerCase()}</p>
                  </div>

                  <MenuRender
                    typeOrder={typeOrder}
                    category={category}
                    restaurant={restaurant}
                    userLoyalityPoints={userLoyalityPoints}
                  />
                </Stack>
              );
            })}
          </Stack>
        </Grid>
      </Grid>
      <AddressSelectionModal
        showModal={showModal}
        restaurantId={String(restaurantId)}
        hideModal={() => setShowModal(false)}
        onConfirmAddress={checkRestaurantDelivery}
      />
      <RestaurantErrorModal
        showRestaurantError={showRestaurantError}
        onPressBtn={handlePressbtnModal}
        onCloseModal={handleCloseErrorModal}
        modalTitle={languageData.fr.delivery.deliveryErrorModal.title}
        modalDescription={languageData.fr.delivery.deliveryErrorModal.message}
        modalButtonLabel={
          languageData.fr.delivery.deliveryErrorModal.actionButtonLabel
        }
      />
    </div>
  );
};

export default withNavigateHook(RestaurantMenuPage);